import axios from "axios";
import { useEnrichmentModalStore } from "@/stores/useEnrichmentModalStore";
import { trialUpgradeMessage } from "@/shared/utils/trialLimits";
import {
  showError,
  API_ERROR_TOAST_ID,
  markErrorToastShown,
  toastAiQuota,
  toastApiError,
} from "@/shared/utils/toast";

/** Paths where 401 (e.g. pending trial) must not wipe session and send user to /signup */
function isAuthSoftFailurePath(pathname) {
  if (!pathname) return false;
  if (pathname === "/onboarding" || pathname === "/admin-request" || pathname === "/pricing")
    return true;
  if (pathname.startsWith("/join-organization")) return true;
  return false;
}

/**
 * Long-running generation routes opt out of the default with an explicit timeout.
 * Exported so call sites use the same number the backend does.
 */
export const LONG_REQUEST_TIMEOUT_MS = 900_000;

const axiosInstanceNode = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  // Was 0 (wait forever): a stalled backend left the spinner up with no error path.
  // Sized to cover ordinary CRUD; see LONG_REQUEST_TIMEOUT_MS for generation routes.
  timeout: 120_000,
  headers: {
    "Content-Type": "application/json",
  },
});

// interceptor to add token to Authorization header
axiosInstanceNode.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // For FormData, remove default Content-Type
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
axiosInstanceNode.interceptors.response.use(
  toastAiQuota,
  (err) => {
    // Session expired, invalid token, trial expired, or pending approval
    if (err.response?.status === 401) {
      const code = err.response?.data?.code;
      const pathname = window.location.pathname;

      // Pending / rejected accounts: middleware returns 401; do not log the user out
      if (code === "pending_approval" || code === "access_rejected") {
        return Promise.reject(err);
      }

      const skipAuthRedirect =
        err.config?.skipAuthRedirect === true || isAuthSoftFailurePath(pathname);

      if (!skipAuthRedirect) {
        if (window.isRedirecting) {
          return Promise.reject(err);
        }
        window.isRedirecting = true;
        const serverMessage = err.response?.data?.error;

        let toastMessage = "Your session has expired. Please sign in again.";
        if (code === "trial_expired") {
          toastMessage = serverMessage || "Your trial has ended. Please contact us to continue.";
        }

        // Clear all stored data
        localStorage.clear();
        sessionStorage.clear();
        // Clear React Query cache
        if (window.queryClient) {
          window.queryClient.clear();
        }
        // Dispatch event to notify App.js
        window.dispatchEvent(new Event("tokenExpired"));
        markErrorToastShown(err);
        showError(toastMessage, undefined, { toastId: API_ERROR_TOAST_ID });

        window.location.href = "/signup";
      }

      return Promise.reject(err);
    }

    // Trial limit or paid-only action: offer the upgrade instead of an error toast.
    const upgradeMessage = trialUpgradeMessage(err.response);
    if (upgradeMessage !== undefined) {
      useEnrichmentModalStore.getState().openModal(upgradeMessage);
      markErrorToastShown(err);
      return Promise.reject(err);
    }

    if (err.response?.data?.code === "trial_expired") {
      window.dispatchEvent(new Event("trialExpired"));
      markErrorToastShown(err);
      return Promise.reject(err);
    }

    toastApiError(err);
    return Promise.reject(err);
  },
);

export default axiosInstanceNode;
